import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Alert,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Paper,
  Grid,
  Card,
  CardMedia,
  CardActions,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Close,
  CheckCircle,
  Error as ErrorIcon,
} from '@mui/icons-material';
import axios from 'axios';

const PhotoUploader = ({ itemId, saleId, onUploadComplete, maxFiles = 20, maxSizeMB = 10 }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [errors, setErrors] = useState([]);

  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      const errorMessages = rejectedFiles.map(({ file, errors }) =>
        `${file.name}: ${errors.map(e => e.message).join(', ')}`
      );
      setErrors(errorMessages);
    }

    const filesWithPreview = acceptedFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      status: 'pending'
    }));
    setFiles(prev => [...prev, ...filesWithPreview].slice(0, maxFiles));
  }, [maxFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.heic']
    },
    maxFiles,
    maxSize: maxSizeBytes,
    multiple: true
  });

  const removeFile = (index) => {
    setFiles(prev => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const uploadFiles = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    setErrors([]);

    const formData = new FormData();
    files.forEach((fileObj) => {
      formData.append('files', fileObj.file);
    });

    if (itemId) formData.append('item_id', itemId);
    if (saleId) formData.append('sale_id', saleId);

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'https://api.paceestate.com'}/api/photos/bulk-upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(progress);
          }
        }
      );

      setUploadedFiles(response.data.uploaded);
      if (response.data.errors && response.data.errors.length > 0) {
        setErrors(response.data.errors.map(e => `${e.filename}: ${e.error}`));
      }

      // Clear successful uploads
      setFiles([]);

      if (onUploadComplete) {
        onUploadComplete(response.data.uploaded);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setErrors([error.response?.data?.error || error.message || 'Upload failed']);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <Box>
      {/* Dropzone */}
      <Paper
        {...getRootProps()}
        sx={{
          p: 4,
          mb: 2,
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          bgcolor: isDragActive ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.3s',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'action.hover'
          }
        }}
      >
        <input {...getInputProps()} />
        <Box textAlign="center">
          <CloudUpload sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {isDragActive ? 'Drop files here' : 'Drag & drop photos here'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            or click to select files
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            Supports: PNG, JPG, JPEG, GIF, WEBP, HEIC (max {maxFiles} files, {maxSizeMB}MB each)
          </Typography>
        </Box>
      </Paper>

      {/* File Preview Grid */}
      {files.length > 0 && (
        <Box mb={2}>
          <Typography variant="subtitle1" gutterBottom>
            Selected Files ({files.length})
          </Typography>
          <Grid container spacing={2}>
            {files.map((fileObj, index) => (
              <Grid item xs={6} sm={4} md={3} key={index}>
                <Card>
                  <CardMedia
                    component="img"
                    height="140"
                    image={fileObj.preview}
                    alt={fileObj.file.name}
                  />
                  <CardActions sx={{ justifyContent: 'space-between', px: 1 }}>
                    <Typography variant="caption" noWrap sx={{ flex: 1 }}>
                      {fileObj.file.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeFile(index)}
                      disabled={uploading}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box mt={2} display="flex" gap={2}>
            <Button
              variant="contained"
              onClick={uploadFiles}
              disabled={uploading || files.length === 0}
              startIcon={<CloudUpload />}
            >
              {uploading ? 'Uploading...' : `Upload ${files.length} Photo${files.length > 1 ? 's' : ''}`}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setFiles([])}
              disabled={uploading}
            >
              Clear All
            </Button>
          </Box>

          {uploading && (
            <Box mt={2}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" color="text.secondary" mt={1}>
                {uploadProgress}% uploaded
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Success Messages */}
      {uploadedFiles.length > 0 && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
          Successfully uploaded {uploadedFiles.length} photo{uploadedFiles.length > 1 ? 's' : ''}
        </Alert>
      )}

      {/* Error Messages */}
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<ErrorIcon />}>
          <Typography variant="subtitle2" gutterBottom>
            {errors.length} error{errors.length > 1 ? 's' : ''} occurred:
          </Typography>
          {errors.map((err, idx) => (
            <Typography key={idx} variant="caption" display="block">
              • {err}
            </Typography>
          ))}
        </Alert>
      )}
    </Box>
  );
};

export default PhotoUploader;
